import { useEffect, useMemo, useState, useRef } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { createUseStyles } from "react-jss";
import useWindowSize from "../../../../lib/player/src/hooks/useWindowSize";
import useHover from "../../../../lib/player/src/hooks/useHover";
import TextInput from "../inputs/TextInput";
import clsx from "clsx";
import isTouchDevice from "is-touch-device";
import Teams from "./Teams";
import { currentAccount } from "../../../proxies";

export const SIDEBAR_WIDTH = 260;
export const SIDEBAR_EXPANDED = 70;

const useStyles = createUseStyles(theme => ({
  sidebar: {
    display: "flex",
    "background-color": "#252525",
    "flex-direction": "column",
    transition: "width 0.2s ease",
    position: "absolute",
    height: "100%",
    width: props => (props.expand ? SIDEBAR_WIDTH : SIDEBAR_EXPANDED),
    "overflow-y": "hidden",
    "overflow-x": "visible",
    "z-index": "10",

    "& hr": {
      width: SIDEBAR_EXPANDED - 30,
      margin: 15,
    },
  },
  search: {
    margin: [-10, 15, 15],
    display: props => (props.expand ? "initial" : "none"),
  },
  searchIcon: {
    margin: [0, 15],
    color: "white",
    "font-size": 18,
    width: SIDEBAR_EXPANDED - 30,
    height: SIDEBAR_EXPANDED - 30,
    "background-color": "#3a3a3a",
    "border-radius": 8,
    display: "flex",
    "align-items": "center",
    "justify-content": "center",
    "flex-shrink": 0,
  },
  accounts: {
    display: "flex",
    "flex-direction": "column",
    width: "100%",
    "max-height": "calc(100vh - 122px)",
    "overflow-x": "hidden",
    "z-index": "10",
    "overflow-y": props =>
      isTouchDevice() || props.hovering ? "auto" : "hidden",
  },
  account: {
    "background-color": "#252525",
    padding: [5, 0],
    "margin-bottom": 5,
    display: "flex",
    "flex-direction": "row",
    "align-items": "center",
    width: SIDEBAR_WIDTH - 8,
    height: SIDEBAR_EXPANDED - 30,
    "flex-shrink": 0,
    transition: "background-color 0.2s ease",
    cursor: "pointer",
    "border-left": "solid transparent",
    "border-left-width": props => (props.expand ? 8 : 3),

    "&:hover": {
      "background-color": "#3a3a3a",
    },

    "& img": {
      "max-height": SIDEBAR_EXPANDED - 30,
      "max-width": SIDEBAR_EXPANDED - 30,
      "border-radius": 3,
      margin: props => (props.expand ? "0 15px 0 7px" : "0 15px 0 12px"),
    },

    "& $svg": {
      color: "#f90",
      margin: props => (props.expand ? "0 15px 0 7px" : "0 15px 0 12px"),
      width: SIDEBAR_EXPANDED - 32,
      height: SIDEBAR_EXPANDED - 32,
      border: "1px solid #3a3a3a",
      "border-radius": 3,
      display: "flex",
      "align-items": "center",
      "justify-content": "center",

      "& svg": {
        height: "100%",
      },
    },

    "& p": {
      color: "white",
      margin: 0,
      "padding-right": 25,
    },
  },
  svg: {},
  active: {
    "border-left-color": theme.colorRed,
    "background-color": props => (props.expand ? "#313131" : ""),
  },
  label: {
    color: "white",
    margin: [0, 0, 10, 15],
    "text-transform": "uppercase",
    "font-size": 14,
  },
  history: {
    color: "white",
    margin: [0, 0, 5, 15],
    "font-size": 14,
    cursor: "pointer",
    display: "flex",
    "flex-direction": "row",
    "align-items": "center",
    "justify-content": "space-between",

    "& svg": {
      float: "right",
      cursor: "pointer",
      "margin-right": 15,
      opacity: 0,
      transition: "opacity 0.1s ease",
    },

    "&:hover": {
      "& svg": {
        opacity: 1,
      },
    },
  },
}));

const Sidebar = ({
  searching,
  accounts,
  search,
  setSearch,
  clearSearch,
  setFocused,
  active,
  setActive,
  activeTeam,
  setActiveTeam,
}) => {
  const [hoverRef, hovering] = useHover();
  const focusRef = useRef();
  const { width } = useWindowSize();
  const expand = width > 800 || hovering;
  const classes = useStyles({ expand, hovering });
  const [history, setHistory] = useState(
    JSON.parse(window.localStorage.getItem("searchHistory") ?? "[]").map(
      text => ({
        id: Math.random(),
        text,
      }),
    ),
  );

  const defaultAccount = useMemo(() => {
    const stored = window.localStorage.getItem("latestAccount");
    let defaultAccount = null;
    if (accounts.map(x => x.token).includes(stored)) {
      defaultAccount = accounts.find(acc => acc.token === stored);
    } else if (accounts.length > 0) {
      defaultAccount = accounts[0];
    }
    return defaultAccount;
  }, [accounts]);

  useEffect(() => {
    if (!active && defaultAccount) {
      setActive(defaultAccount);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, defaultAccount]);

  useEffect(() => {
    if (active) {
      window.localStorage.setItem("latestAccount", active);
      currentAccount.account = active;
    }
  }, [active]);

  useEffect(() => {
    window.localStorage.setItem(
      "searchHistory",
      JSON.stringify(history.map(({ text }) => text)),
    );
  }, [history]);

  useEffect(() => {
    const current = focusRef.current;
    const onFocus = () => setFocused(true);
    const onBlur = () => setFocused(false);
    if (current) {
      current.addEventListener("focus", onFocus);
      current.addEventListener("blur", onBlur);
    }

    return () => {
      if (current) {
        current.removeEventListener("focus", onFocus);
        current.removeEventListener("blur", onBlur);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expand]);

  return (
    <div className={classes.sidebar} ref={hoverRef}>
      <div className={classes.search}>
        <form
          onSubmit={evt => {
            evt.preventDefault();
            if (search.length > 2) {
              setHistory(h => [{ id: Math.random(), text: search }, ...h]);
            }
          }}
        >
          <TextInput
            ref={focusRef}
            label="search"
            value={search}
            onChange={x => setSearch(x)}
            clearButton={searching}
            clear={clearSearch}
          />
        </form>
      </div>
      {expand || (
        <>
          <div
            className={classes.searchIcon}
            onClick={() => {
              if (searching) {
                clearSearch();
              } else {
                focusRef.current.focus();
              }
            }}
          >
            <FontAwesomeIcon
              icon={searching ? ["fal", "times"] : ["fal", "search"]}
            />
          </div>
          {searching || <hr />}
        </>
      )}
      {expand && searching && <p className={classes.label}>Search History</p>}
      {searching ? (
        expand &&
        history.map(({ id, text }) => (
          <p
            key={id}
            className={classes.history}
            onClick={evt => {
              evt.stopPropagation();
              evt.preventDefault();
              setSearch(text);
              focusRef.current.focus();
            }}
          >
            {text}
            <FontAwesomeIcon
              onClick={evt => {
                evt.stopPropagation();
                setHistory(h => h.filter(x => x.id !== id));
              }}
              icon={["fal", "times-circle"]}
            />
          </p>
        ))
      ) : (
        <div className={classes.accounts}>
          {accounts.map(account => (
            <div key={account.id}>
              <div
                className={clsx(classes.account, {
                  [classes.active]: active === account.token,
                })}
                onClick={() => setActive(account)}
              >
                {account.logo ? (
                  <img src={account.logo} alt={account.name} />
                ) : (
                  <div className={classes.svg}>
                    <FontAwesomeIcon icon={["fal", "building"]} fixedWidth />
                  </div>
                )}
                <p>{account.name}</p>
              </div>
              {active === account.token && (
                <Teams
                  show={expand}
                  account={account.id}
                  active={activeTeam?.id}
                  setActive={setActiveTeam}
                />
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default Sidebar;
